import { MutableRefObject, useCallback, useMemo } from 'react';

import { GridApi, GridColumnResizeParams } from '@mui/x-data-grid-premium';

import useGeneratikTableStore, {
  buildColumnWidthKey,
} from '@/shared/ui/GeneratikTable/generatikTableStore';

import { isPeriodField } from '../timeline/timeline.utils';
import { INTERNAL_FIELDS } from './headersApiAdapter';

export interface UseColumnWidthPersistenceParams {
  pageType: string | undefined;
  resourceId: string | undefined;
  apiRef: MutableRefObject<GridApi>;
}

export interface UseColumnWidthPersistenceReturn {
  storedWidths: Record<string, number> | undefined;
  handleColumnWidthChange: (params: GridColumnResizeParams) => void;
}

const useColumnWidthPersistence = ({
  pageType,
  resourceId,
  apiRef,
}: UseColumnWidthPersistenceParams): UseColumnWidthPersistenceReturn => {
  const widthKey = useMemo(
    () => (pageType ? buildColumnWidthKey(pageType, resourceId) : null),
    [pageType, resourceId],
  );

  const storedWidths = useGeneratikTableStore((state) =>
    widthKey ? state.columnWidths[widthKey] : undefined,
  );
  const setColumnWidths = useGeneratikTableStore((state) => state.setColumnWidths);

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      if (!widthKey) return;

      // Seed with the previous snapshot: hidden columns render at computedWidth 0
      // and would otherwise drop out, coming back at their narrow defaults (and a
      // squeezed, scroll-less table) when re-shown.
      const snapshot: Record<string, number> = { ...storedWidths };
      try {
        for (const col of apiRef.current.getAllColumns()) {
          if (INTERNAL_FIELDS.has(col.field) || isPeriodField(col.field)) continue;
          const width =
            typeof col.computedWidth === 'number' && col.computedWidth > 0
              ? col.computedWidth
              : col.width;
          if (typeof width === 'number' && width > 0) {
            snapshot[col.field] = Math.round(width);
          }
        }
      } catch {
        // apiRef not ready — fall back to single-column write
      }

      snapshot[params.colDef.field] = Math.round(params.width);
      setColumnWidths(widthKey, snapshot);
    },
    [widthKey, storedWidths, setColumnWidths, apiRef],
  );

  return { storedWidths, handleColumnWidthChange };
};

export default useColumnWidthPersistence;
